import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Grid, useTheme, Box } from '@mui/material';
import { MarketLink, MarketTooltip } from '@market/react';
import QuantitySelector, {
  QUANTITY_SELECTOR_SIZES,
} from 'components/common/quantity_selector';
import { shopImageSrcSet } from 'utilities/images';
import { toCurrency } from 'utilities/currency';
import { SHIPPING_QUOTE_TYPES } from 'constants/enums';
import { localizedShopPath } from 'utilities/locales';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import {
  buildPricingLabel,
  buildTrialDuration,
  getSoftwareTypeIcon,
  getCombinedPlanPrice,
  getKioskPnpBreakdown,
} from 'utilities/software';
import { onCartPageClick } from 'components/pages/cart/utilities';
import { useFeatureFlags } from 'components/context/feature_flags';

const ActionableItem = (props) => {
  const { t, i18n } = useTranslation();
  const { s3bKioskSoftwareRenameToKioskLite: kioskLiteEnabled } =
    useFeatureFlags();
  const theme = useTheme();

  const {
    token,
    img,
    slug,
    hasPdp,
    qty,
    limit,
    title,
    price,
    currency,
    outOfStock,
    disclaimer,
    shipQuote,
    parts,
    plan,
    softwareGroup,
    onQtyChange,
    disabled,
    strikethroughPrice,
    isSoftware,
    isPnpSubscribed,
    locationCount,
    kioskDeviceCount,
    bopisEligible,
    isReadOnly,
  } = props;

  const itemLink = `${localizedShopPath()}/products/${slug}`;

  const itemState = (outOfStock, shipQuote, disclaimer, bopisEligible) => {
    if (outOfStock) {
      return (
        <p className="medium-20" style={{ color: '#D64040' }}>
          {t('cart.label.outOfStockItem')}
        </p>
      );
    } else if (shipQuote) {
      let quoteText;
      if (shipQuote.type === SHIPPING_QUOTE_TYPES.PREORDER) {
        quoteText = i18n.format(
          t(`checkout.shippingEstimate.tag.${shipQuote.verb}.multiple`, {
            min: shipQuote.min,
            max: shipQuote.max,
            time_unit: t(
              `shipping.shippingEstimate.timeUnits.${shipQuote.unit}.other`,
            ),
          }),
          'capitalize',
        );
      } else {
        quoteText = t('cart.label.extendedShipQuoteItem', {
          min: shipQuote.min,
          max: shipQuote.max,
          unit: t(
            `shipping.shippingEstimate.timeUnits.${shipQuote.unit}.other`,
          ),
        });
      }

      return (
        <div
          className="medium-20"
          style={{ display: 'flex', alignItems: 'center', color: '#ED770B' }}
        >
          <span>{quoteText}</span>
          {disclaimer && (
            <MarketTooltip>
              <span slot="content">{disclaimer}</span>
            </MarketTooltip>
          )}
        </div>
      );
    } else {
      return (
        <p className="medium-20" style={{ color: '#00B23B' }}>
          {bopisEligible
            ? `${t('cart.label.inStockItem')} - ${t('cart.label.inStorePickupAvailable')}`
            : t('cart.label.inStockItem')}
        </p>
      );
    }
  };

  const removeButton = (
    <MarketLink
      {...(disabled && { disabled: true })}
      onClick={(e) => {
        e.preventDefault();
        onQtyChange(token, qty, 0);
        onCartPageClick({
          actionItem: 'remove',
          additionalParameters: {
            token,
            slug,
            quantity: qty,
          },
        });
      }}
      className="medium-20"
    >
      {t('cart.label.remove')}
    </MarketLink>
  );

  const priceDisplay = () => {
    let pricingContent;
    let textAlignStyle = { textAlign: 'right' };

    if (isSoftware && plan) {
      pricingContent = (
        <p>
          {getCombinedPlanPrice(plan, {
            isPnpSubscribed,
            locationCount,
            kioskDeviceCount,
          })}
        </p>
      );
    } else if (isSoftware) {
      // The plan was not loaded from the source; add handling if required.
      pricingContent = (
        <p>
          <Trans i18nKey={'cart.software.label.pricingUnavailable'} />
        </p>
      );
      textAlignStyle = { textAlign: 'center' };
    } else {
      // Display pricing for a regular hardware product in the shop.
      pricingContent = (
        <>
          <p>{toCurrency(price, currency)}</p>
          {strikethroughPrice && (
            <p style={{ color: theme.color.gray['55'] }}>
              <s>{toCurrency(strikethroughPrice, currency)}</s>
            </p>
          )}
        </>
      );
    }

    return (
      <Grid item xs={6} sm={2} sx={textAlignStyle}>
        {pricingContent}
      </Grid>
    );
  };

  const priceLabelDisplay = () => {
    if (!isSoftware || !plan) {
      return null;
    }

    const breakdown = getKioskPnpBreakdown({
      plan,
      kioskLiteEnabled,
      t,
      isPnpSubscribed,
    });
    if (breakdown) {
      const trialMessage = plan.free_trial_duration
        ? buildTrialDuration(t, plan.free_trial_duration)
        : null;

      return (
        <>
          {trialMessage && <p className="paragraph-20">{trialMessage}</p>}
          {breakdown.map((line, index) => (
            <p key={index} className="paragraph-20">
              {line.cartLabel}
            </p>
          ))}
        </>
      );
    }

    return (
      <p className="paragraph-20">
        {buildPricingLabel(t, plan, kioskLiteEnabled)}
      </p>
    );
  };

  const getThumbnail = () => {
    if (isSoftware) {
      const SoftwareIcon = getSoftwareTypeIcon(softwareGroup);
      return SoftwareIcon ? (
        <SoftwareIcon />
      ) : (
        <img
          alt={title}
          srcSet={shopImageSrcSet({ baseUrl: img, type: 'cartSoftwareTile' })}
          width="32px"
          height="32px"
        />
      );
    }

    const productImg = (
      <img
        alt={title}
        srcSet={shopImageSrcSet({ baseUrl: img, type: 'cartProductTile' })}
        width="110px"
        height="110px"
      />
    );

    if (hasPdp && !isDashboardMode()) {
      return <a href={itemLink}>{productImg}</a>;
    }

    return productImg;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-end"
      px={2}
      py={isSoftware ? 3 : 1}
    >
      <Grid container alignItems="center">
        <Grid
          item
          xs={4}
          sm={2.5}
          alignItems="center"
          justifyContent="center"
          display="flex"
        >
          {getThumbnail()}
        </Grid>
        <Grid item xs={8} sm={9.5} container rowSpacing={1} alignItems="center">
          <Grid item xs={12} sm={isSoftware ? 10 : 6}>
            {!isSoftware &&
              itemState(outOfStock, shipQuote, disclaimer, bopisEligible)}
            {hasPdp && !isDashboardMode() ? (
              <a
                href={itemLink}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <p className="medium-30">{title}</p>
              </a>
            ) : (
              <p className="medium-30">{title}</p>
            )}
            {!isSoftware && (
              <p className="paragraph-20">
                {parts.map(({ name }) => name).join(', ')}
              </p>
            )}
            {priceLabelDisplay()}
          </Grid>
          <Grid
            item
            xs={12}
            sm={isSoftware ? 2 : 6}
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid
              item
              container
              alignItems="center"
              flexWrap="nowrap"
              sx={{
                justifyContent: isSoftware ? 'flex-end' : 'space-between',
                pl: { xs: 0, sm: '10%' },
              }}
            >
              {!isSoftware &&
                (isReadOnly ? (
                  <p className="medium-20">
                    {`${t('cart.label.quantity')}: ${qty}`}
                  </p>
                ) : (
                  <QuantitySelector
                    value={qty}
                    disabled={disabled || outOfStock || limit === 1}
                    onValueChange={(newQty) => onQtyChange(token, qty, newQty)}
                    minimumValue={1}
                    maximumValue={limit}
                    size={QUANTITY_SELECTOR_SIZES.medium}
                  />
                ))}
              <Grid item display="flex" direction="column" alignItems="center">
                {priceDisplay()}
                {!isReadOnly && removeButton}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActionableItem;
